// A saved conversation, read-only.
//
// ★ IT RENDERS THE SURFACES, NOT JUST THE CAPTION. What the engine publishes per turn is the user's
//   text, `finalCaption`, and the turn's RENDER surfaces — chart specs as sent, the preview table,
//   assumption lines, the result basis (loop.js `_renderForTranscript`). So a saved chart turn draws
//   its chart and a saved listing draws its table, through the SAME components the live thread uses.
//   Storing the caption alone was a measured defect in its own right: a turn whose answer was a KPI
//   card and a 125-row preview replayed as the sentence "I've shown the matching records below",
//   below which there was nothing.
//
// ★ ONE TABLE PER TURN, DECIDED BY THE SAME RULE. `reconcileRenderSurfaces` reads only `charts` and
//   `exports`, so a message-shaped object built from the stored render gets the identical verdict a
//   live turn got: a real chart suppresses the export table, a listing keeps it, a lone `table`
//   chart renders only when no export panel takes its place. Re-deciding it here would be a second
//   copy of that rule, free to drift.
//
// ★ IT IS STILL NOT THE LIVE THREAD, and the differences are deliberate, not missing work:
//   · NO Download CSV — the stored descriptor is trimmed to what renders and carries no query, and
//     a download on a historical turn would re-read the database under today's privileges.
//   · NO follow-up chips — a suggestion is an invitation to send, and this surface cannot send.
//   · NO thinking steps, no cost readout, no SQL accordion — none is part of the answer.
//   · A conversation saved BEFORE render surfaces were kept has captions only; the note says so
//     rather than leaving the reader to wonder where the chart went.
//
// ★ WITH CONTINUING CHATS OFF IT CANNOT BE CONTINUED: conversation state lives in the engine's memory, keyed by conversationId, and
//   a chat the server never saw answers `unknown_conversation` on its next turn. ON (the server says so — RSM4, design v2 D18),
//   SqAiPage draws this as the history ABOVE the live thread of the same chat (`continuing`): the record as it was answered, its
//   figures as recorded, with no note telling the reader to start a new chat. Another user's chat stays read-only, saying whose it
//   is (`readOnlyLabel`). An ask the chat ended on is drawn as its text, as every saved answer is — no buttons (D17).

import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useNearEdge, scrollParent } from "../useNearEdge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { reconcileRenderSurfaces } from "@safetyqube/sq-ai-react";
import styles from "../sqAi.module.css";
import ChartCard from "./chart/ChartCard";
import ExportPanel from "./ExportPanel";
import MessageTime from "./MessageTime";
import { splitFollowups } from "../followupBlock.mjs";

// One saved assistant turn, in the live thread's order: assumptions, charts, prose, table, follow-ups —
// the follow-up block cut from the caption's tail and drawn under the table by the same rule as Thread's.
// `render.resultMeta` is deliberately not read: a turn saved before the engine stopped sending the
// "Counted by" tag and the coverage sentence still carries them, and nothing is drawn under an answer.
function SavedAssistantTurn({ message, zone, askedText, onRetry }) {
  const render = message.render || {};
  const caption = (message.content || "").trim();
  // `exports` is the wire's name for the array reconcileRenderSurfaces reads; the transcript keeps
  // the single descriptor as `table`, so it is wrapped here rather than stored twice.
  const { exportDescriptor, showExportPanel, chartsToRender } = reconcileRenderSurfaces({
    charts: Array.isArray(render.charts) ? render.charts : [],
    exports: render.table ? [render.table] : null,
  });
  const assumptions = Array.isArray(render.assumptions) ? render.assumptions : [];
  const { body, followups } = showExportPanel ? splitFollowups(caption) : { body: caption, followups: "" };

  // ★ A TURN THAT STORED NOTHING TO SHOW is a turn that was stopped — drawn as that, never as an empty bubble (RSM10, BT1 13): no
  //   caption, no chart, no table, no assumption. The words are the product's; the retry asks the same question again where the
  //   page can (a chat that can be continued, or a new chat), and is absent where it cannot.
  if (!caption && !chartsToRender.length && !showExportPanel && !assumptions.length) {
    return (
      <div className={styles.interrupted} role="status">
        <span>This answer was interrupted — ask again</span>
        {onRetry && askedText && (
          <button type="button" className={styles.ghostBtn} onClick={() => onRetry(askedText)}>
            Ask again
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {assumptions.map((a, i) => (
        <div className={styles.assumption} key={i}>
          <span aria-hidden="true">ⓘ</span>
          <span>
            {a.text}
            {a.rationale && <span className={styles.assumptionRationale}> — {a.rationale}</span>}
          </span>
        </div>
      ))}

      {/* `done` is true by construction: a saved turn has finished. It gates the card's export bar,
          and the PNG/PDF buttons work here because they capture the rendered node client-side. */}
      {chartsToRender.map((spec, i) => (
        <ChartCard key={i} spec={spec} exportMeta={exportDescriptor ? exportDescriptor.exportMeta : null} done />
      ))}

      {body && (
        <div className={`${styles.prose} ${styles.assistantCaption}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      )}

      {showExportPanel && <ExportPanel descriptor={exportDescriptor} zone={zone} readOnly />}

      {followups && (
        <div className={`${styles.prose} ${styles.assistantCaption}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{followups}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

// How long a freshly opened chat keeps itself pinned to its newest turn while charts and tables take their height.
const SETTLE_MS = 3000;

export default function HistoryTranscript({
  messages,
  loading,
  error,
  // The reader's zone for each message's time — the page's own timezone setting.
  userTz,
  // Paging walks BACKWARDS through the transcript, so this control sits at the TOP: it loads what
  // came BEFORE what is on screen, which is where those turns belong once they arrive.
  hasEarlier,
  loadingEarlier,
  onLoadEarlier,
  // The turns above failed to load: the transcript stays and a button at its top asks again (the scroll no longer does).
  earlierFailed = false,
  // Asks a question again (an interrupted answer's); absent where the page cannot send it.
  onRetry,
  // Continuing chats (RSM4): this transcript is the history of the chat the reader is continuing; or, read-only, whose it is.
  continuing = false,
  readOnlyLabel = null,
}) {
  // Whether this conversation carries any render surfaces at all. A chat saved before they were
  // published has none, and that is worth SAYING — the alternative is a reader concluding the
  // charts are broken.
  const anyRender = messages.some((m) => m.role === "assistant" && m.render);
  const anyAssistant = messages.some((m) => m.role === "assistant");

  // Earlier turns load by scrolling UP to the top of the transcript.
  const { sentinelRef, scrollRef } = useNearEdge({
    enabled: Boolean(hasEarlier) && !loadingEarlier && !earlierFailed,
    onReach: () => {
      // ★ MEASURED BEFORE THE FETCH, RESTORED AFTER THE PREPEND. Inserting content ABOVE the
      //   reader moves everything they were looking at down by the height of what arrived, so
      //   without this the view jumps and — worse — the sentinel is pushed back into range, which
      //   fires the next page immediately and walks the whole transcript in one gesture.
      const sc = scrollRef.current;
      anchorRef.current = sc ? { height: sc.scrollHeight, top: sc.scrollTop } : null;
      onLoadEarlier();
    },
    // This list grows upward, so the top is the edge that asks for earlier turns.
    edge: "top",
  });
  const anchorRef = useRef(null);
  const rootRef = useRef(null);
  const openedAtNewestRef = useRef(false);
  const settleRef = useRef(null);

  // ★ A SAVED CHAT OPENS AT ITS NEWEST MESSAGE, like the live thread it is a record of — and this
  //   is load-bearing, not decoration. Left at the top, the reader would face the OLDEST turn of
  //   the first page, and the top edge would already be within the trigger threshold, so every
  //   remaining page would load at once and paging would be defeated on open.
  //   Once per conversation: SqAiPage keys this component on the conversation id, so a different
  //   chat mounts a fresh component and this runs again for it.
  useLayoutEffect(() => {
    if (openedAtNewestRef.current || loading || !messages.length) return;
    const sc = scrollRef.current || scrollParent(rootRef.current);
    if (!sc) return;
    scrollRef.current = sc;
    sc.scrollTop = sc.scrollHeight;
    openedAtNewestRef.current = true;
    // ★ ...AND STAYS THERE WHILE IT SETTLES (RSM10, item 3). Charts and tables take their height after this runs, so a single pin
    //   left the newest turn partly below the fold on exactly the chats with charts. The root is watched and re-pinned on every
    //   resize until the reader touches the scroll (wheel, touch, key, drag) or the page has been still for a while; a reader who
    //   scrolled is never pulled back.
    if (typeof ResizeObserver !== "function") return;
    const stop = () => settleRef.current && settleRef.current();
    const ro = new ResizeObserver(() => {
      sc.scrollTop = sc.scrollHeight;
    });
    ro.observe(rootRef.current);
    const events = ["wheel", "touchstart", "keydown", "mousedown"];
    events.forEach((e) => sc.addEventListener(e, stop, { passive: true }));
    const timer = setTimeout(stop, SETTLE_MS);
    settleRef.current = () => {
      ro.disconnect();
      clearTimeout(timer);
      events.forEach((e) => sc.removeEventListener(e, stop));
      settleRef.current = null;
    };
  }, [loading, messages, scrollRef]);
  useEffect(() => () => settleRef.current && settleRef.current(), []);

  // Runs before paint, so the correction is never visible as a flicker.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const sc = scrollRef.current;
    if (!anchor || !sc) return;
    anchorRef.current = null;
    sc.scrollTop = anchor.top + (sc.scrollHeight - anchor.height);
  }, [messages, scrollRef]);

  return (
    <div className={styles.thread} ref={rootRef}>
      {loading && <p className={styles.railNote}>Loading this chat…</p>}
      {!loading && error && <p className={styles.railNote}>{error}</p>}

      {!loading && !error && (
        <>
          {/* Stated once, at the top, because it applies to everything below it. Two different
              sentences for two different situations — a chat that predates render surfaces is not
              the same thing as one whose limits are only the CSV and the follow-ups. */}
          {readOnlyLabel && <p className={styles.notice}>{readOnlyLabel}</p>}
          {!continuing && (
            <p className={styles.historyHint}>
              {anyAssistant && !anyRender
                ? "A saved chat from before charts and tables were kept — the answers are here as text. Start a new chat to see them drawn."
                : "A saved chat, as it was answered. Downloads and follow-ups aren't part of the record — start a new chat to ask again."}
            </p>
          )}

          {/* ABOVE the messages, because this list grows upward: scrolling past it asks for the
              turns that came before. Absent once there are none, which is how a transcript says
              it starts here. */}
          {hasEarlier && !earlierFailed && (
            <div ref={sentinelRef} className={styles.transcriptSentinel} aria-hidden="true">
              {loadingEarlier && <span className={styles.spinner} role="status" aria-label="Loading earlier messages" />}
            </div>
          )}
          {hasEarlier && earlierFailed && (
            <div className={styles.transcriptSentinel}>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => {
                  const sc = scrollRef.current;
                  anchorRef.current = sc ? { height: sc.scrollHeight, top: sc.scrollTop } : null;
                  onLoadEarlier();
                }}
              >
                Load earlier
              </button>
            </div>
          )}

          {messages.map((m, i) => (
            <div className={styles.row} key={m.id}>
              {m.role === "user" ? (
                <div className={styles.userRow}>
                  <div className={styles.userTurn}>
                    <div className={styles.userBubble}>{m.content}</div>
                    <MessageTime at={m.createdAt} zone={userTz} side="user" />
                  </div>
                </div>
              ) : (
                // A saved turn's time is the store's `createdAt` — when the turn was WRITTEN, which is when
                // it finished; its question carries the same moment (the engine publishes both at the end).
                <>
                  <SavedAssistantTurn message={m} zone={userTz} onRetry={onRetry} askedText={(messages[i - 1] && messages[i - 1].role === "user" && messages[i - 1].content) || ""} />
                  <MessageTime at={m.createdAt} zone={userTz} side="answer" />
                </>
              )}
            </div>
          ))}

          {messages.length === 0 && !continuing && <p className={styles.railNote}>This chat has no stored messages.</p>}
        </>
      )}
    </div>
  );
}
